// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip, Button, CircularProgress } from '@mui/material';
import { type AccountInfo } from '@shared/data-access/interfaces/api/brokerage/account';
import { useAccounts, AccountsTable } from '@advisor-app/features/accounts';
import {
  ClientsTable,
  useListClients,
} from '@shared/advisory/aggregation/components/clients';
import { ButtonLinkAccount } from '@advisor-app/features/brokers';
import {
  IconButton,
  KpiBoxes,
  PageContainer,
  SearchBar,
  SectionContainer,
  WrapperEffect,
} from '@shared/ui-kit/components';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { useAppNavigate } from '@advisor-app/hooks';
import { useCallback, useMemo } from 'react';
import {
  selectAccountId,
  selectAccounts,
  selectBroker,
} from '@advisor-app/store/slices/broker';
import { useAppSelector } from '@advisor-app/store/store';
import { useAppDispatch } from '@advisor-app/store';
import type { ClientRow } from '@shared/advisory/aggregation/hooks/use-clients';
import { userSlice, selectAccountCurrency } from '@shared/data-access/slices/user';
import { clientsSlice } from '@shared/data-access/slices/clients/clients-slice';
import { useAdvisorRole } from '@advisor-app/hooks';
import { useTranslation } from 'react-i18next';

export const Accounts = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const broker = useAppSelector(selectBroker);
  const accounts = useAppSelector(selectAccounts);
  const accountId = useAppSelector(selectAccountId);
  const currency = useAppSelector(selectAccountCurrency);

  const { setAccountOnRedux, isLoadingAccounts } = useAccounts();

  const { role } = useAdvisorRole();

  const handleSetClientProfile = useCallback(
    (client: ClientRow, shouldNavigate = true) => {
      dispatch(
        clientsSlice.actions.setClient({
          id: client.id,
          name: client.name,
          email: client.email,
          citec_user_id: client.citec_user_id,
          risk_profile: client.risk_profile,
          status: client.status,
        }),
      );
      dispatch(userSlice.actions.setDashboard('aggregator'));

      if (shouldNavigate) {
        navigate('/aggregation');
      }
    },
    [dispatch, navigate],
  );

  const handleViewProfile = useCallback(
    (client: ClientRow) => {
      dispatch(
        clientsSlice.actions.setClient({
          id: client.id,
          name: client.name,
          email: client.email,
          citec_user_id: client.citec_user_id,
          risk_profile: client.risk_profile,
          status: client.status,
        }),
      );
      navigate('/profile');
    },
    [dispatch, navigate],
  );

  const handleSelectClient = useCallback(
    (client: ClientRow) => {
      dispatch(
        clientsSlice.actions.setClient({
          id: client.id,
          name: client.name,
          email: client.email,
          citec_user_id: client.citec_user_id,
          risk_profile: client.risk_profile,
          status: client.status,
        }),
      );
    },
    [dispatch],
  );

  const {
    clients,
    status: clientsStatus,
    hasData: clientsHasData,
    onRefresh: onRefreshClients,
    kpis: kpiData,
    textSearch,
    onTextSearch,
    onDeleteClient,
    onAddCitecUserClient,
    onSetClientProfile,
    onViewProfile,
    userId,
    pagination,
    pageCount,
    onPaginationChange,
  } = useListClients({
    onSetClientProfile: handleSetClientProfile,
    onViewProfile: handleViewProfile,
    onSelectClient: handleSelectClient,
  });

  const onRefresh = onRefreshClients;

  const availableBrokers = {
    alpaca: 'Alpaca',
    tradier: 'Tradier',
    interactive_brokers: 'Interactive Brokers',
    '': 'Broker',
  };

  const filteredData = useMemo(() => {
    if (!textSearch.trim()) return accounts;

    return accounts.filter(account => {
      const accountName = account.name?.toLowerCase() || broker || '';
      const searchTerm = textSearch.toLowerCase().trim();

      return accountName.includes(searchTerm);
    });
  }, [accounts, broker, textSearch]);

  const handleSetAccount = useCallback(
    /* istanbul ignore next */
    (accountInfo: AccountInfo[]) => {
      setAccountOnRedux(
        accountInfo[0] ?? {
          account_id: '',
          currency: '',
          account_type: '',
          name: '',
          last_name: '',
          account_manager: '',
          investment: 0,
          unrealized_capital_gains: 0,
          buying_power: 0,
          cash: 0,
          aum: 0,
          local_currency_values: {
            buying_power: 0,
            cash: 0,
            aum: 0,
            investment: 0,
            unrealized_capital_gains: 0,
            currency: '',
          },
        },
      );
      if (accountInfo.length) {
        dispatch(userSlice.actions.setDashboard('broker'));
        navigate('/dashboard');
      }
    },
    [dispatch, navigate, setAccountOnRedux],
  );

  return (
    <PageContainer
      titlePage={t('accounts.manage-accounts', { ns: 'components' })}
      actionBtn={
        <div className="flex items-center gap-4">
          <SearchBar
            placeholder={t('accounts.search-client', { ns: 'components' })}
            value={textSearch}
            onChangeValue={onTextSearch}
            dataTestId="client-search-bar"
          />
          <ButtonLinkAccount />
        </div>
      }
    >
      <div className="mb-10 grid grid-cols-2 gap-6 md:grid-cols-4">
        {kpiData.map((vals, idx) => (
          <KpiBoxes key={idx} {...vals} />
        ))}
      </div>

      {!!broker && (
        <SectionContainer
          className="mb-10"
          title={`${availableBrokers[broker as keyof typeof availableBrokers]} ${t(
            'common.accounts',
            { ns: 'common' },
          )}`}
          headerClass="mb-7"
          actions={
            <div className="flex items-center gap-4">
              <Tooltip
                title={t('accounts.download-accounts', { ns: 'components' })}
              >
                <IconButton
                  icon="Download"
                  onClick={() => exportTableToExcel(accounts, 'accounts')}
                  data-testid="download-accounts-button"
                />
              </Tooltip>
            </div>
          }
        >
          <WrapperEffect showIn={!isLoadingAccounts}>
            <AccountsTable
              data={filteredData}
              onSetAccount={handleSetAccount}
              broker={broker}
              accountId={accountId}
            />
          </WrapperEffect>
        </SectionContainer>
      )}

      <SectionContainer
        title={
          <span className="flex items-center gap-x-2">
            {t('accounts.list-of-clients', { ns: 'components' })}
          </span>
        }
        headerClass="mb-7"
        actions={
          <div className="flex items-center gap-x-4">
            <Button
              variant="outlined"
              color="secondary"
              onClick={onAddCitecUserClient}
              data-testid="invite-client-button"
              size="medium"
            >
              {t('accounts.invite-new-client', { ns: 'components' })}
            </Button>
            {clientsStatus === 'fetching' ? (
              <CircularProgress size={20} data-testid="loading-spinner" />
            ) : (
              <Tooltip
                title={t('common.refresh', { ns: 'common' })}
                placement="top"
              >
                <span className="flex items-center justify-center">
                  <IconButton
                    icon="RefreshTwoArrows"
                    variant="icon"
                    size="xl"
                    color={clientsStatus === 'pending' ? 'disabled' : 'main'}
                    onClick={onRefresh}
                    data-testid="refresh-btn"
                    disabled={clientsStatus === 'pending'}
                  />
                </span>
              </Tooltip>
            )}
          </div>
        }
      >
        <WrapperEffect showIn={clientsStatus !== 'pending' || clientsHasData}>
          <ClientsTable
            data={clients}
            userId={userId}
            role={role}
            onDeleteClient={onDeleteClient}
            onViewProfile={onViewProfile}
            onSetClientProfile={onSetClientProfile}
            currency={currency}
            pagination={pagination}
            pageCount={pageCount}
            onPaginationChange={onPaginationChange}
          />
        </WrapperEffect>
      </SectionContainer>
    </PageContainer>
  );
};

export default Accounts;
